import React from 'react'
import { BellOutlined, CheckCircleOutlined, WarningOutlined, FlagOutlined } from '@ant-design/icons'
import { MdOutlinePlayArrow } from 'react-icons/md'

/**
 * Status transitions for the assignment view drawer.
 * Each transition becomes a button visible only when the current status equals `from`.
 */
const TRANSITIONS = [
    { from: 'assigned_executor', to: 'execution_started', label: 'Розпочати виконання' },
    { from: 'execution_started', to: 'loading_started', label: 'Розпочати завантаження' },
    { from: 'loading_started', to: 'arrived_at_unloading', label: 'Прибув на розвантаження' },
    { from: 'arrived_at_unloading', to: 'unloading_started', label: 'Розпочати розвантаження' },
    { from: 'unloading_started', to: 'loading_started', label: 'Нова ходка (завантаження)' },
    { from: 'unloading_started', to: 'completed_by_driver', label: 'Завершити (водій)' },
]

/**
 * Builds the buttons map for the assignment view drawer.
 * Extracted from `AssignmentResource.tsx` to keep the resource declaration thin
 * (ADR-002 §І-4 — no thick logic inside a Resource).
 *
 * Wired through `AssignmentResource.detailConfig.modeConfig.view.onButtons`.
 *
 * У шапці картки (960px) лишаються лише головні дії: переходи з поточного статусу (один,
 * з unloading_started — два) і підтвердження/завершення; push, примусове підтвердження й
 * «Повідомити про затримку» — у меню «⋯» (priority: 'dropdown'). Кнопки, що не вміщаються,
 * переносяться на новий рядок (Ship ButtonGroup). Вирішити активну затримку можна і з банера.
 */
export const buildAssignmentViewButtons = ({ buttons, crudContext }: any) => {
    const depStore = { formContext: crudContext.formContext, crudContext }

    TRANSITIONS.forEach(({ from, to, label }, index) => {
        buttons[`changeStatus_${from}_${to}`] = {
            label,
            icon: React.createElement(MdOutlinePlayArrow),
            variant: 'default',
            size: 'middle',
            priority: 'button',
            sort: 10 + index,
            depStore,
            depFn: ({ formContext }: any) => {
                const permissions = formContext?.values?._permissions || {}
                return !!permissions.change_status && formContext?.values?.status === from
            },
            onClick: () => crudContext.eventManager.emit('assignmentAction:changeStatus', { status: to }),
        }
    })

    buttons.sendPush = {
        label: 'Push-повідомлення',
        icon: React.createElement(BellOutlined),
        variant: 'default',
        size: 'middle',
        priority: 'dropdown',
        sort: 100,
        depStore,
        depFn: ({ formContext }: any) => {
            const status = formContext?.values?.status
            const permissions = formContext?.values?._permissions || {}
            // send_push comes from ExecutorAssignmentRule — view-only roles
            // (ROLE_VIEWER) must not see the button (BUG-10).
            return !!permissions.send_push && !!status && status !== 'completed_by_dispatcher'
        },
        onClick: () => crudContext.eventManager.emit('assignmentAction:openPushModal'),
    }

    buttons.confirmExecution = {
        label: 'Підтвердити виконання',
        icon: React.createElement(CheckCircleOutlined),
        variant: 'primary',
        size: 'middle',
        priority: 'button',
        sort: 200,
        depStore,
        depFn: ({ formContext }: any) => {
            const status = formContext?.values?.status
            const permissions = formContext?.values?._permissions || {}
            return !!permissions.confirm_execution && status === 'completed_by_driver'
        },
        onClick: () => crudContext.eventManager.emit('assignmentAction:openConfirmModal'),
    }

    buttons.confirmExecutionForce = {
        label: 'Підтвердити виконання (примусово)',
        icon: React.createElement(CheckCircleOutlined),
        variant: 'primary',
        size: 'middle',
        priority: 'dropdown',
        sort: 201,
        depStore,
        depFn: ({ formContext }: any) => {
            const status = formContext?.values?.status
            const permissions = formContext?.values?._permissions || {}
            return (
                !!permissions.confirm_execution &&
                !!status &&
                status !== 'completed_by_driver' &&
                status !== 'completed_by_dispatcher'
            )
        },
        onClick: () => crudContext.eventManager.emit('assignmentAction:openConfirmModal'),
    }

    // ── Driver-role buttons ────────────────────────────────────────────────────
    buttons.reportDelay = {
        label: 'Повідомити про затримку',
        icon: React.createElement(WarningOutlined),
        variant: 'danger',
        size: 'middle',
        priority: 'dropdown',
        sort: 50,
        depStore,
        depFn: ({ formContext }: any) => {
            const status = formContext?.values?.status
            const permissions = formContext?.values?._permissions || {}
            return (
                !!permissions.create_delay &&
                !!status &&
                status !== 'completed_by_driver' &&
                status !== 'completed_by_dispatcher'
            )
        },
        onClick: () => crudContext.eventManager.emit('assignmentAction:openReportDelayModal'),
    }

    buttons.completeTask = {
        label: 'Завершити завдання',
        icon: React.createElement(FlagOutlined),
        variant: 'primary',
        size: 'middle',
        priority: 'button',
        sort: 60,
        depStore,
        depFn: ({ formContext }: any) => {
            const status = formContext?.values?.status
            const permissions = formContext?.values?._permissions || {}
            return !!permissions.create_rating && status === 'unloading_started'
        },
        onClick: () => crudContext.eventManager.emit('assignmentAction:openCompleteTaskModal'),
    }
}
